import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useApp } from '../../contexts/AppContext';
import { IafDataSource, IafFetch } from '../../services/platformApi';
import {
  ORCHESTRATOR_USER_TYPES,
  SCRIPT_NAMES,
  TASK_USER_TYPES,
  DEFAULT_SCHEDULED_ORCHESTRATOR,
  DEFAULT_INSTANT_ORCHESTRATOR,
  getEmptyOrchestratorForm,
  formatSchedule,
  parseSchedule,
} from './constants';
import type {
  Orchestrator,
  OrchestratorFormData,
  OrchestratorConfig,
  OrchestratorRun,
} from './types';
import './OrchestratorManagementPage.css';

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export function OrchestratorManagementPage() {
  const { createCtx, accessToken } = useAuth();
  const { selectedProject, applicationNamespaces } = useApp();

  // State
  const [orchestrators, setOrchestrators] = useState<Orchestrator[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Form state
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState<OrchestratorFormData>(getEmptyOrchestratorForm());

  // Run state
  const [runningOrchestratorId, setRunningOrchestratorId] = useState<string | null>(null);
  const [lastRunResult, setLastRunResult] = useState<OrchestratorRun | null>(null);

  // Get project namespaces only (orchestrators require single project namespace)
  const getProjectNamespaces = useCallback(() => {
    return selectedProject?._namespaces || [];
  }, [selectedProject]);

  // Create context for API calls - use only project namespaces for orchestrators
  const getCtx = useCallback(() => {
    const namespaces = getProjectNamespaces();
    return createCtx(namespaces);
  }, [createCtx, getProjectNamespaces]);

  // ============================================================================
  // API CALLS
  // ============================================================================

  const fetchOrchestrators = useCallback(async () => {
    if (!accessToken || !selectedProject) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      
      // Use IafDataSource.getOrchestrators to fetch orchestrators
      const response = await IafDataSource.getOrchestrators({}, ctx, { _pageSize: 100 });

      console.log('Orchestrators response:', response);

      // Extract list from response
      const allOrchestrators = response?._list || [];

      // Filter to only show orchestrators (datasources with _instant or _orchschedule)
      const orchList = allOrchestrators.filter((d: Orchestrator) => 
        d._userType?.includes('telemetry_sim') || 
        d._userType?.includes('orchestrator') ||
        d._instant !== undefined ||
        d._orchschedule !== undefined
      );

      setOrchestrators(orchList);
    } catch (err) {
      console.error('Error fetching orchestrators:', err);
      setError(`Failed to fetch orchestrators: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, selectedProject, getCtx]);

  const createOrchestrator = async (config: OrchestratorConfig): Promise<Orchestrator | null> => {
    if (!accessToken || !selectedProject) {
      setError('No project selected');
      return null;
    }

    const ctx = getCtx();

    // First, check if orchestrator with same userType exists and delete it
    const existingOrch = orchestrators.find(o => o._userType === config._userType);
    if (existingOrch) {
      console.log('Deleting existing orchestrator:', existingOrch.id);
      try {
        await IafDataSource.deleteOrchestrator(existingOrch.id, ctx);
      } catch (e) {
        console.warn('Failed to delete existing orchestrator:', e);
      }
    }

    // Create new orchestrator using IafDataSource
    const result = await IafDataSource.createOrchestrator(config, ctx);
    console.log('Create orchestrator result:', result);
    return result;
  };

  const runOrchestrator = async (orchestrator: Orchestrator) => {
    if (!accessToken) {
      setError('Not authenticated');
      return;
    }

    setRunningOrchestratorId(orchestrator.id);
    setError(null);
    setLastRunResult(null);

    try {
      const ctx = getCtx();

      const result = await IafDataSource.runOrchestrator(
        orchestrator.id,
        { orchestratorId: orchestrator.id },
        ctx
      );

      // Log full result to console for debugging
      console.log('='.repeat(60));
      console.log('ORCHESTRATOR RUN RESULT');
      console.log('='.repeat(60));
      console.log('Orchestrator:', orchestrator._name);
      console.log('ID:', orchestrator.id);
      console.log('Result:', result);
      console.log('='.repeat(60));
      
      setLastRunResult(result);
      setSuccessMessage(`Orchestrator "${orchestrator._name}" triggered successfully! Check console for details.`);
    } catch (err) {
      console.error('Error running orchestrator:', err);
      setError(`Failed to run orchestrator: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setRunningOrchestratorId(null);
    }
  };

  const deleteOrchestrator = async (orchestrator: Orchestrator) => {
    if (!accessToken || !confirm(`Delete orchestrator "${orchestrator._name}"?`)) return;

    setIsLoading(true);
    setError(null);

    try {
      const ctx = getCtx();
      await IafDataSource.deleteOrchestrator(orchestrator.id, ctx);

      setSuccessMessage(`Orchestrator "${orchestrator._name}" deleted!`);
      await fetchOrchestrators();
    } catch (err) {
      console.error('Error deleting orchestrator:', err);
      setError(`Failed to delete orchestrator: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // FORM HANDLERS
  // ============================================================================

  const handleCreateScheduled = () => {
    setFormData(DEFAULT_SCHEDULED_ORCHESTRATOR);
    setIsCreating(true);
  };

  const handleCreateInstant = () => {
    setFormData(DEFAULT_INSTANT_ORCHESTRATOR);
    setIsCreating(true);
  };

  const handleCreateCustom = () => {
    setFormData(getEmptyOrchestratorForm());
    setIsCreating(true);
  };

  const handleSubmitForm = async () => {
    if (!formData.name || !formData.userType) {
      setError('Name and User Type are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const baseUri = IafFetch.CONFIG.itemServiceOrigin;
      const namespaces = getProjectNamespaces();

      const config: OrchestratorConfig = {
        _name: formData.name,
        _description: formData.description,
        _namespaces: namespaces,
        _userType: formData.userType,
        _params: {
          tasks: [
            {
              name: 'default_script_target',
              _actualparams: {
                userType: formData.taskUserType,
                _scriptName: formData.taskScriptName,
                baseUri,
                dryRun: formData.dryRun,
              },
              _sequenceno: 1,
            },
          ],
        },
      };

      if (formData.isInstant) {
        config._instant = true;
      } else {
        config._timeout = '1 * *';
        config._orchschedule = {
          runinterval: formatSchedule(formData.scheduleHours, formData.scheduleMinutes, formData.scheduleSeconds),
        };
      }

      console.log('Creating orchestrator:', config);
      await createOrchestrator(config);

      setSuccessMessage(`Orchestrator "${formData.name}" created successfully!`);
      setIsCreating(false);
      setFormData(getEmptyOrchestratorForm());
      await fetchOrchestrators();
    } catch (err) {
      console.error('Error creating orchestrator:', err);
      setError(`Failed to create orchestrator: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================================================
  // EFFECTS
  // ============================================================================

  useEffect(() => {
    fetchOrchestrators();
  }, [fetchOrchestrators]);

  // ============================================================================
  // HELPERS
  // ============================================================================

  const hasScheduledOrchestrator = orchestrators.some(
    o => o._userType === ORCHESTRATOR_USER_TYPES.SIM_AUTO_PUBLISH
  );

  const hasInstantOrchestrator = orchestrators.some(
    o => o._userType === ORCHESTRATOR_USER_TYPES.SIM_INSTANT
  );

  const getOrchestratorTypeLabel = (orch: Orchestrator): string => {
    if (orch._instant) return 'Instant';
    if (orch._orchschedule) return 'Scheduled';
    return 'Unknown';
  };

  const getScheduleDisplay = (orch: Orchestrator): string => {
    if (!orch._orchschedule?.runinterval) return '-';
    const { hours, minutes, seconds } = parseSchedule(orch._orchschedule.runinterval);
    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (minutes > 0) parts.push(`${minutes}m`);
    if (seconds > 0) parts.push(`${seconds}s`);
    return parts.length > 0 ? `Every ${parts.join(' ')}` : 'Continuous';
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  if (!selectedProject) {
    return (
      <div className="orchestrator-management">
        <div className="orchestrator-empty-state">
          <i className="fas fa-exclamation-triangle"></i>
          <h2>No Project Selected</h2>
          <p>Please select a project to manage orchestrators.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="orchestrator-management">
      <div className="orchestrator-header">
        <div className="orchestrator-title">
          <i className="fas fa-clock"></i>
          <h1>Orchestrator Management</h1>
        </div>
        <p className="orchestrator-subtitle">
          Manage simulation orchestrators for{' '}
          <strong>{selectedProject._name}</strong>
        </p>
      </div>

      {/* Messages */}
      {error && (
        <div className="orchestrator-message error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}
      {successMessage && (
        <div className="orchestrator-message success">
          <i className="fas fa-check-circle"></i>
          <span>{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      {/* Quick Create Section */}
      <div className="section-card">
        <h2>
          <i className="fas fa-magic"></i>
          Quick Create
        </h2>
        <p className="section-description">
          Create pre-configured orchestrators for telemetry simulation.
        </p>

        <div className="quick-create-grid">
          <div className={`quick-create-card ${hasScheduledOrchestrator ? 'exists' : ''}`}>
            <div className="card-icon">
              <i className="fas fa-calendar-alt"></i>
            </div>
            <h3>Scheduled Orchestrator</h3>
            <p>Auto-publishes telemetry data on a schedule (default: every 30 minutes)</p>
            {hasScheduledOrchestrator ? (
              <span className="exists-badge">
                <i className="fas fa-check"></i> Already exists
              </span>
            ) : (
              <button className="btn-primary" onClick={handleCreateScheduled} disabled={isLoading}>
                <i className="fas fa-plus"></i>
                Create
              </button>
            )}
          </div>

          <div className={`quick-create-card ${hasInstantOrchestrator ? 'exists' : ''}`}>
            <div className="card-icon instant">
              <i className="fas fa-bolt"></i>
            </div>
            <h3>Instant Orchestrator</h3>
            <p>Manually trigger telemetry simulation on demand</p>
            {hasInstantOrchestrator ? (
              <span className="exists-badge">
                <i className="fas fa-check"></i> Already exists
              </span>
            ) : (
              <button className="btn-primary" onClick={handleCreateInstant} disabled={isLoading}>
                <i className="fas fa-plus"></i>
                Create
              </button>
            )}
          </div>

          <div className="quick-create-card custom">
            <div className="card-icon custom">
              <i className="fas fa-cog"></i>
            </div>
            <h3>Custom Orchestrator</h3>
            <p>Create a custom orchestrator with your own configuration</p>
            <button className="btn-secondary" onClick={handleCreateCustom} disabled={isLoading}>
              <i className="fas fa-plus"></i>
              Create Custom
            </button>
          </div>
        </div>
      </div>

      {/* Orchestrators List */}
      <div className="section-card">
        <div className="section-header">
          <h2>
            <i className="fas fa-list"></i>
            Orchestrators ({orchestrators.length})
          </h2>
          <button className="btn-secondary" onClick={fetchOrchestrators} disabled={isLoading}>
            <i className={`fas fa-sync-alt ${isLoading ? 'fa-spin' : ''}`}></i>
            Refresh
          </button>
        </div>

        {isLoading && orchestrators.length === 0 ? (
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Loading orchestrators...</span>
          </div>
        ) : orchestrators.length === 0 ? (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No orchestrators found</p>
            <p className="hint">Use Quick Create above to get started</p>
          </div>
        ) : (
          <div className="orchestrators-table">
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>User Type</th>
                  <th>Schedule</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orchestrators.map((orch) => (
                  <tr key={orch.id}>
                    <td>
                      <div className="orch-name">
                        <strong>{orch._name}</strong>
                        {orch._description && (
                          <span className="orch-desc">{orch._description}</span>
                        )}
                      </div>
                    </td>
                    <td>
                      <span className={`type-badge ${orch._instant ? 'instant' : 'scheduled'}`}>
                        {getOrchestratorTypeLabel(orch)}
                      </span>
                    </td>
                    <td>
                      <code>{orch._userType}</code>
                    </td>
                    <td>{getScheduleDisplay(orch)}</td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-primary small"
                          onClick={() => runOrchestrator(orch)}
                          disabled={runningOrchestratorId === orch.id}
                          title="Run orchestrator now"
                        >
                          {runningOrchestratorId === orch.id ? (
                            <i className="fas fa-spinner fa-spin"></i>
                          ) : (
                            <i className="fas fa-play"></i>
                          )}
                          Run
                        </button>
                        <button
                          className="btn-icon danger"
                          onClick={() => deleteOrchestrator(orch)}
                          title="Delete orchestrator"
                        >
                          <i className="fas fa-trash"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Last Run Result */}
      {lastRunResult && (
        <div className="section-card">
          <h2>
            <i className="fas fa-terminal"></i>
            Last Run Result
          </h2>
          <pre className="run-result">
            {JSON.stringify(lastRunResult, null, 2)}
          </pre>
        </div>
      )}

      {/* Create Modal */}
      {isCreating && (
        <div className="modal-overlay" onClick={() => setIsCreating(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>
                <i className="fas fa-plus-circle"></i>
                Create Orchestrator
              </h2>
              <button className="btn-icon" onClick={() => setIsCreating(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Name*</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Orchestrator name"
                  />
                </div>
                <div className="form-group">
                  <label>User Type*</label>
                  <input
                    type="text"
                    value={formData.userType}
                    onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                    placeholder="e.g., telemetry_sim_instant"
                  />
                </div>
                <div className="form-group full-width">
                  <label>Description</label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe what this orchestrator does..."
                    rows={2}
                  />
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={formData.isInstant}
                        onChange={() => setFormData({ ...formData, isInstant: true })}
                      />
                      <span>Instant (manual trigger)</span>
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        checked={!formData.isInstant}
                        onChange={() => setFormData({ ...formData, isInstant: false })}
                      />
                      <span>Scheduled (automatic)</span>
                    </label>
                  </div>
                </div>

                {!formData.isInstant && (
                  <div className="form-group">
                    <label>Schedule Interval</label>
                    <div className="schedule-inputs">
                      <div className="schedule-field">
                        <input
                          type="number"
                          min={0}
                          max={23}
                          value={formData.scheduleHours}
                          onChange={(e) => setFormData({ ...formData, scheduleHours: parseInt(e.target.value) || 0 })}
                        />
                        <span>hours</span>
                      </div>
                      <div className="schedule-field">
                        <input
                          type="number"
                          min={0}
                          max={59}
                          value={formData.scheduleMinutes}
                          onChange={(e) => setFormData({ ...formData, scheduleMinutes: parseInt(e.target.value) || 0 })}
                        />
                        <span>minutes</span>
                      </div>
                      <div className="schedule-field">
                        <input
                          type="number"
                          min={0}
                          max={59}
                          value={formData.scheduleSeconds}
                          onChange={(e) => setFormData({ ...formData, scheduleSeconds: parseInt(e.target.value) || 0 })}
                        />
                        <span>seconds</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="form-section">
                <h3>Task Configuration</h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label>Script Name</label>
                    <input
                      type="text"
                      value={formData.taskScriptName}
                      onChange={(e) => setFormData({ ...formData, taskScriptName: e.target.value })}
                      placeholder="e.g., runAhuChillerSimulationPeriod"
                    />
                    <span className="form-hint">
                      Default: {SCRIPT_NAMES.AHU_CHILLER_SIM}
                    </span>
                  </div>
                  <div className="form-group">
                    <label>Task User Type</label>
                    <input
                      type="text"
                      value={formData.taskUserType}
                      onChange={(e) => setFormData({ ...formData, taskUserType: e.target.value })}
                      placeholder="e.g., sim_ahu_chiller"
                    />
                    <span className="form-hint">
                      Default: {TASK_USER_TYPES.SIM_AHU_CHILLER}
                    </span>
                  </div>
                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.dryRun}
                        onChange={(e) => setFormData({ ...formData, dryRun: e.target.checked })}
                      />
                      <span>Dry Run (no data written)</span>
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setIsCreating(false)}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSubmitForm}
                disabled={isLoading || !formData.name || !formData.userType}
              >
                {isLoading ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-plus"></i>}
                Create Orchestrator
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OrchestratorManagementPage;
